import React, { FC, useEffect, useState } from 'react';
import { Theme } from '@mui/material';
import { makeStyles } from '@mui/styles';

import { observer, inject } from 'mobx-react';

import Header from 'components/common/Header';
import Footer from 'components/common/Footer';
import StoreModel from '../models/Index';
import { Services } from '../helpers/constants/Services';
import { Route, Switch, useLocation } from 'react-router';
import IdleMonitor from '../components/common/IdleMonitor';
import CashAcceptorEmulator from '../components/CashAcceptorEmulator';
import { RoutePaths } from 'helpers/constants/RoutePaths';
import IdleScreen from 'containers/common/IdleScreen';
import ApplicationSelect from 'containers/common/ApplicationSelect';
import LanguageSelect from 'containers/common/LanguageSelect';
import KioskOutOfOrder from 'containers/common/KioskOutOfOrder';
import PaymentKioskPage from './PaymentKioskPage';
import Loader from 'components/common/Loader';
import { ThemeType } from 'interfaces';
import {
    Collection,
    CollectionSuccess,
    CollectionSummary,
} from 'containers/common';
import CashAcceptorCassetteEventsEmulator from 'components/CashAcceptorCassetteEventsEmulator';

interface CommonFlowProps {}
interface CommonFlowPropsInjected extends CommonFlowProps {
    Store: StoreModel;
}
let showHeader: boolean;
let showFooter: boolean;

const useStyles = makeStyles((theme: Theme) => {
    return {
        headerBox: {
            height: '15vh',
        },
        header: {
            display: 'flex',
            justifyContent: 'space-between',
            height: 'calc(100% - 8px)',
            backgroundColor: (theme: ThemeType) =>
                showHeader
                    ? theme.header.backgroundColor
                    : theme.backgroundColor,
        },
        headerLine: {
            height: '8px',
            backgroundColor: (theme: ThemeType) =>
                showHeader ? theme.header.borderColor : theme.backgroundColor,
        },

        root: {
            display: 'flex',
            flexDirection: 'column',
            height: '100vh',
            overflow: 'auto',
        },

        footerBox: {
            height: '10vh',
        },

        footer: {
            display: 'flex',
            alignItems: 'center',
            height: 'calc(100% - 2px)',
            width: '100%',
            justifyContent: 'space-between',
            padding: 'calc(10vh/100*5) 10% calc(10vh/100*5) 10%',
            boxSizing: 'border-box',
            backgroundColor: (theme: ThemeType) =>
                showFooter
                    ? theme.footer.backgroundColor
                    : theme.backgroundColor,
        },
        footerLine: {
            height: '2px',
            backgroundColor: (theme: ThemeType) =>
                showFooter ? theme.footer.borderColor : theme.backgroundColor,
        },
    };
});

const CommonFlow: FC<CommonFlowProps> = (props) => {
    const { Store } = props as CommonFlowPropsInjected;
    const { showIdleMonitor, configuration, shellClient, deviceProfile } =
        Store;
    const [initialized, setInitialized] = useState<boolean>(false);

    useEffect(() => {
        Store.loader.show();
        Store.initCommonFlow().finally(() => {
            setInitialized(true);
            Store.loader.hide();
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const classes = useStyles(deviceProfile.theme);

    const { pathname } = useLocation();

    const pathnamesWithOutHeader = [
        RoutePaths.IdleScreen,
        RoutePaths.LanguageSelect,
        RoutePaths.CashCollection,
        RoutePaths.CashCollectionSummary,
        RoutePaths.CashCollectionSuccess,
    ];

    const pathnamesWithOutFooter = [
        RoutePaths.IdleScreen,
        RoutePaths.LanguageSelect,
        RoutePaths.CashCollection,
        RoutePaths.CashCollectionSummary,
        RoutePaths.KioskOutOfOrder,
        RoutePaths.CashCollectionSuccess,
    ];

    showHeader = !pathnamesWithOutHeader.includes(pathname);
    showFooter = !pathnamesWithOutFooter.includes(pathname);

    return initialized ? (
        <>
            <div className={classes.root}>
                {showHeader && (
                    <div className={classes.headerBox}>
                        <div className={classes.header}>
                            <Header Store={Store} />
                        </div>
                        <div className={classes.headerLine}></div>
                    </div>
                )}

                <Switch>
                    <Route
                        exact
                        path={RoutePaths.IdleScreen}
                        component={IdleScreen}
                    />
                    <Route
                        path={RoutePaths.ApplicationSelect}
                        component={ApplicationSelect}
                    />
                    <Route
                        path={RoutePaths.LanguageSelect}
                        component={LanguageSelect}
                    />
                    <Route
                        path={RoutePaths.KioskOutOfOrder}
                        component={KioskOutOfOrder}
                    />
                    <Route
                        path={RoutePaths.CashAccepterStep}
                        component={PaymentKioskPage}
                    />
                    <Route
                        path={RoutePaths.CashCollection}
                        component={Collection}
                    />
                    <Route
                        path={RoutePaths.CashCollectionSummary}
                        component={CollectionSummary}
                    />
                    <Route
                        path={RoutePaths.CashCollectionSuccess}
                        component={CollectionSuccess}
                    />
                </Switch>
                {showFooter && (
                    <div className={classes.footerBox}>
                        <div className={classes.footerLine}></div>
                        <div className={classes.footer}>
                            <Footer Store={Store} />
                        </div>
                    </div>
                )}
            </div>

            {showIdleMonitor && (
                <IdleMonitor
                    inactivityTime={configuration.app.inactivityTime}
                    idleWaitTime={configuration.app.idleWaitTime}
                    store={Store}
                />
            )}

            {shellClient.isEmulation && <CashAcceptorEmulator />}
            {shellClient.isEmulation && <CashAcceptorCassetteEventsEmulator />}
        </>
    ) : (
        <Loader loader={Store.loader} />
    );
};

export default inject(Services.STORE)(observer(CommonFlow));
